import React, { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { loadingToggleAction, loginAction, socialLoginAction } from '../../store/actions/AuthActions';
import { checkUserSignInMethods } from '../../services/AuthService';
import PropTypes from 'prop-types';
import { Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle, Info } from 'lucide-react';
import useWindowSize, { isMobile } from '../../hooks/useWindowSize';

// image
import loginbg from "../../images/bg-login.jpg";

// Enhanced CSS styles
const styles = {
  loginMainPage: {
    minHeight: '100vh',
    display: 'flex',
    backgroundImage: `url(${loginbg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  },
  loginWrapper: {
    display: 'flex',
    width: '100%',
    minHeight: '100vh'
  },
  loginAsideLeft: {
    flex: '1',
    background: 'rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    color: 'white'
  },
  loginAsideRight: {
    flex: '1',
    background: 'rgba(255, 255, 255, 0.98)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backdropFilter: 'blur(10px)'
  },
  authCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '3rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '450px',
    border: '1px solid #e5e7eb'
  },
  formGroup: {
    marginBottom: '1.5rem',
    position: 'relative'
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  formInput: {
    width: '100%',
    padding: '0.875rem 1rem 0.875rem 3rem',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    backgroundColor: '#fafafa'
  },
  inputIcon: {
    position: 'absolute',
    left: '1rem',
    color: '#9ca3af',
    zIndex: 2
  },
  eyeIcon: {
    position: 'absolute',
    right: '1rem',
    color: '#9ca3af',
    cursor: 'pointer',
    zIndex: 2,
    background: 'none',
    border: 'none'
  },
  submitButton: {
    width: '100%',
    padding: '0.875rem',
    background: 'linear-gradient(135deg,#057e38 0%, var(--bs-primary) 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    marginTop: '1rem'
  },
  socialButton: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#fff',
    color: '#374151',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    transition: 'all 0.2s ease',
    marginBottom: '0.75rem'
  },
  googleOnlyButton: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#4285f4',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    transition: 'all 0.2s ease',
    marginTop: '1rem'
  },
  separator: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    color: '#9ca3af',
    margin: '1.5rem 0'
  },
  separatorLine: {
    flex: 1,
    height: '1px',
    background: '#e5e7eb'
  },
  separatorText: {
    padding: '0 1rem',
    fontSize: '0.875rem',
    backgroundColor: 'white'
  },
  inlineError: {
    color: '#dc2626',
    fontSize: '0.875rem',
    marginTop: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem'
  },
  inlineInfo: {
    color: '#2563eb',
    fontSize: '0.875rem',
    marginTop: '0.5rem',
    padding: '0.75rem',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem'
  },
  alertMessage: {
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    fontSize: '0.875rem'
  },
  errorAlert: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca'
  },
  successAlert: {
    backgroundColor: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0'
  },
  loadingSpinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid currentColor',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

// Mobile-specific style overrides (applied when screen ≤ 768px)
const mobileStyles = {
  loginWrapper: {
    flexDirection: 'column'
  },
  loginAsideLeft: {
    minHeight: '200px',
    padding: '2rem 1.5rem',
    flex: 'none'
  },
  loginAsideRight: {
    padding: '1.5rem 1rem',
    flex: 'none'
  },
  authCard: {
    padding: '2rem 1.5rem',
    boxShadow: 'none',
    borderRadius: '0'
  },
  formInput: {
    fontSize: '16px' // Prevents zoom on iOS
  }
};

// Helper function to merge mobile styles
const getResponsiveStyle = (baseStyle, mobileStyle, isMobile) => {
  return isMobile && mobileStyle ? { ...baseStyle, ...mobileStyle } : baseStyle;
};

function Login(props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showGoogleOnlyPrompt, setShowGoogleOnlyPrompt] = useState(false);
  const [googleOnlyEmail, setGoogleOnlyEmail] = useState('');
  let errorsObj = { email: '', password: '' };
  const [errors, setErrors] = useState(errorsObj);

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  
  // Get window size for responsive design
  const { width } = useWindowSize();
  const mobile = isMobile(width);

  console.log('Login component rendered with props:', props);

  const redirectUrl = new URLSearchParams(location.search).get('redirect') || '/';

  const actuallyLoading = isLoading || props.showLoading;

  // Security: Input sanitization
  const sanitizeInput = (input) => {
    return input.trim().substring(0, 255); // Limit length and trim
  };

  // Security: Email validation with enhanced checks
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254; // RFC 5321 limit
  };

  async function onLogin(e) {
    e.preventDefault();

    setIsLoading(true);

    // Reset states
    setErrors(errorsObj);
    setShowGoogleOnlyPrompt(false);

    let error = false;
    const errorObj = { ...errorsObj };

    // Enhanced validation with security measures
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedPassword = sanitizeInput(password);

    if (!sanitizedEmail) {
      errorObj.email = 'E-postadress krävs';
      error = true;
    } else if (!isValidEmail(sanitizedEmail)) {
      errorObj.email = 'Ogiltig e-postadress';
      error = true;
    }

    if (!sanitizedPassword) {
      errorObj.password = 'Lösenord krävs';
      error = true;
    } else if (sanitizedPassword.length < 6) {
      errorObj.password = 'Lösenordet måste vara minst 6 tecken';
      error = true;
    }

    setErrors(errorObj);

    if (error) {
      setIsLoading(false);
      return;
    }

    dispatch(loadingToggleAction(true));

    try {
      // Check user's sign-in methods before attempting login
      const signInMethods = await checkUserSignInMethods(sanitizedEmail);

      // If user only has Google sign-in, show Google login prompt
      if (signInMethods.hasGoogle && !signInMethods.hasPassword) {
        setShowGoogleOnlyPrompt(true);
        setGoogleOnlyEmail(sanitizedEmail);
        setIsLoading(false);
        dispatch(loadingToggleAction(false));

        // Auto-trigger Google sign-in after a brief delay for UX
        setTimeout(() => {
          handleSocialLogin('google');
        }, 1000);

        return;
      }

      // Proceed with regular email/password login
      dispatch(loginAction(sanitizedEmail, sanitizedPassword, history, redirectUrl));
    } catch (error) {
      console.error('Sign-in method check failed:', error);
      // Fallback to regular login attempt if check fails
      dispatch(loginAction(sanitizedEmail, sanitizedPassword, history, redirectUrl));
    }
  }

  function handleSocialLogin(provider) {
    setIsLoading(true);
    setShowGoogleOnlyPrompt(false);
    dispatch(loadingToggleAction(true));
    dispatch(socialLoginAction(provider, history, redirectUrl));
  }

  // Reset loading state when component updates
  React.useEffect(() => {
    if (!props.showLoading) {
      setIsLoading(false);
    }
  }, [props.showLoading]);

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .form-input:focus {
          outline: none;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          background-color: white;
        }
        
        .submit-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
        }
        
        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        .social-button:hover:not(:disabled) {
          border-color: var(--bs-primary);
          background-color: #f8fafc;
        }
        
        .google-only-button:hover:not(:disabled) {
          background-color: #3367d6;
        }
        
        .google-only-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .social-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .error-input {
          border-color: #dc2626 !important;
          background-color: #fef2f2 !important;
        }
        
        @media (max-width: 768px) {
          .login-aside-left {
            display: none;
          }
          
          .auth-card {
            padding: 2rem;
            margin: 1rem;
          }
        }
      `}</style>

      <div style={styles.loginMainPage} data-testid="login-page">
        <div style={getResponsiveStyle(styles.loginWrapper, mobileStyles.loginWrapper, mobile)}>
          {/* Left Side */}
          <div className="login-aside-left" style={getResponsiveStyle(styles.loginAsideLeft, mobileStyles.loginAsideLeft, mobile)} data-testid="login-branding">
            <div style={{ textAlign: 'center', maxWidth: '400px' }}>
              <h1 style={{
                fontSize: '3rem',
                fontWeight: '700',
                marginBottom: '1rem',
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                color: 'white'
              }} data-testid="app-title">
                LedgerLoop AI
              </h1>
              <p style={{
                fontSize: '1.25rem',
                marginBottom: '3rem',
                opacity: 0.95,
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                lineHeight: 1.4
              }} data-testid="app-description">
                Spara tid varje dag med automatiserad bokföring, löner och fakturering
              </p>

              <div style={{
                marginTop: 'auto',
                fontSize: '0.875rem',
                opacity: 0.8,
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }} data-testid="footer-info">
                <p style={{ marginBottom: '1rem' }} data-testid="copyright">
                  © 2025 Riza Consulting AB
                </p>
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <a
                    href="https://www.ledgerloop.ai/privacy"
                    style={{
                      color: 'rgba(255,255,255,0.9)',
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                    data-testid="privacy-link"
                  >
                    Integritetspolicy
                  </a>
                  <span style={{ opacity: 0.7 }}>•</span>
                  <a
                    href="https://www.ledgerloop.ai/terms"
                    style={{
                      color: 'rgba(255,255,255,0.9)',
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                    data-testid="terms-link"
                  >
                    Användarvillkor
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side */}
          <div className="login-aside-right" style={getResponsiveStyle(styles.loginAsideRight, mobileStyles.loginAsideRight, mobile)} data-testid="login-form-container">
            <div style={getResponsiveStyle(styles.authCard, mobileStyles.authCard, mobile)} data-testid="auth-card">
              <div style={{ marginBottom: '2rem', textAlign: 'center' }} data-testid="login-header">
                <h2 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
                  Välkommen tillbaka
                </h2>
                <p style={{ color: '#6b7280', fontSize: '1rem' }}>
                  Logga in för att komma åt ditt konto
                </p>
              </div>

              {/* Alert Messages */}
              {props.errorMessage && (
                <div style={{ ...styles.alertMessage, ...styles.errorAlert }} data-testid="error-message" role="alert">
                  <AlertCircle size={20} />
                  {props.errorMessage}
                </div>
              )}

              {props.successMessage && (
                <div style={{ ...styles.alertMessage, ...styles.successAlert }} data-testid="success-message" role="alert">
                  <CheckCircle size={20} />
                  {props.successMessage}
                </div>
              )}

              {/* Google-only account prompt */}
              {showGoogleOnlyPrompt && (
                <div style={styles.inlineInfo} data-testid="google-only-prompt">
                  <Info size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                      Detta konto använder Google för inloggning
                    </div>
                    <div data-testid="google-only-email">
                      Vi öppnar Google-inloggning automatiskt för {googleOnlyEmail}
                    </div>
                  </div>
                </div>
              )}

              {/* Login Form */}
              {!showGoogleOnlyPrompt && (
                <form onSubmit={onLogin} role='form' data-testid="login-form" noValidate>
                  <div style={styles.formGroup}>
                    <label
                      htmlFor="email-input"
                      style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}
                    >
                      E-postadress
                    </label>
                    <div style={styles.inputWrapper}>
                      <Mail size={20} style={styles.inputIcon} />
                      <input
                        id="email-input"
                        data-testid="email-input"
                        type="email"
                        className={`form-input ${errors.email ? 'error-input' : ''}`}
                        style={getResponsiveStyle(styles.formInput, mobileStyles.formInput, mobile)}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="din@epost.se"
                        disabled={actuallyLoading}
                        maxLength="254"
                        autoComplete="email"
                      />
                    </div>
                    {errors.email && (
                      <div style={styles.inlineError} data-testid="email-error">
                        <AlertCircle size={16} />
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div style={styles.formGroup}>
                    <label
                      htmlFor="password-input"
                      style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}
                    >
                      Lösenord
                    </label>
                    <div style={styles.inputWrapper}>
                      <Lock size={20} style={styles.inputIcon} />
                      <input
                        id="password-input"
                        data-testid="password-input"
                        type={showPassword ? 'text' : 'password'}
                        className={`form-input ${errors.password ? 'error-input' : ''}`}
                        style={getResponsiveStyle(styles.formInput, mobileStyles.formInput, mobile)}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Ditt lösenord"
                        disabled={actuallyLoading}
                        maxLength="255"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        data-testid="password-toggle"
                        style={styles.eyeIcon}
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={actuallyLoading}
                        aria-label={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {errors.password && (
                      <div style={styles.inlineError} data-testid="password-error">
                        <AlertCircle size={16} />
                        {errors.password}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    data-testid="login-submit-button"
                    className="submit-button"
                    style={styles.submitButton}
                    disabled={actuallyLoading}
                  >
                    {actuallyLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={styles.loadingSpinner} data-testid="loading-spinner"></div>
                        Loggar in...
                      </div>
                    ) : (
                      'Logga in'
                    )}
                  </button>

                  {/* Forgot password link */}
                  <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                    <Link
                      to="/forgot-password"
                      data-testid="forgot-password-link"
                      style={{
                        color: '#3b82f6',
                        fontSize: '0.875rem',
                        textDecoration: 'none',
                        fontWeight: '500'
                      }}
                    >
                      Glömt lösenord?
                    </Link>
                  </div>
                </form>
              )}

              {/* Google-only login button */}
              {showGoogleOnlyPrompt && (
                <button
                  type="button"
                  data-testid="google-only-button"
                  className="google-only-button"
                  style={styles.googleOnlyButton}
                  onClick={() => handleSocialLogin('google')}
                  disabled={actuallyLoading || showGoogleOnlyPrompt}
                >
                  {actuallyLoading ? (
                    <div style={styles.loadingSpinner} data-testid="google-loading-spinner"></div>
                  ) : (
                    <i className="fab fa-google" style={{ color: 'white' }}></i>
                  )}
                  Fortsätt med Google
                </button>
              )}

              {/* Registration Link */}
              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  Har du inget konto än?{' '}
                  <Link
                    to={redirectUrl !== '/' ? `/page-register?redirect=${encodeURIComponent(redirectUrl)}` : '/page-register'}
                    data-testid="register-link"
                    style={{ color: '#3b82f6', fontWeight: '600', textDecoration: 'none' }}
                  >
                    Registrera dig här
                  </Link>
                </p>
              </div>

              {/* Separator */}
              <div style={styles.separator} data-testid="separator">
                <div style={styles.separatorLine}></div>
                <span style={styles.separatorText}>eller</span>
                <div style={styles.separatorLine}></div>
              </div>

              {/* Social Login */}
              <button
                type="button"
                data-testid="google-social-button"
                className="social-button"
                style={styles.socialButton}
                onClick={() => handleSocialLogin('google')}
                disabled={isLoading || showGoogleOnlyPrompt}
              >
                <i className="fab fa-google" style={{ color: '#db4437' }}></i>
                Fortsätt med Google
              </button>

              {/* Terms */}
              <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: '#9ca3af' }} data-testid="terms-footer">
                Genom att fortsätta godkänner du våra{' '}
                <a href="https://www.ledgerloop.ai/terms" style={{ color: '#3b82f6' }} data-testid="terms-footer-link">
                  användarvillkor
                </a>{' '}
                och{' '}
                <a href="https://www.ledgerloop.ai/privacy" style={{ color: '#3b82f6' }} data-testid="privacy-footer-link">
                  integritetspolicy
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

Login.propTypes = {
  errorMessage: PropTypes.string,
  successMessage: PropTypes.string,
  showLoading: PropTypes.bool
};

const mapStateToProps = (state) => {
  return {
    errorMessage: state.auth.errorMessage,
    successMessage: state.auth.successMessage,
    showLoading: state.auth.showLoading,
  };
};

export default connect(mapStateToProps)(Login);
